/**
 * useVisualViewportHeight — keep --as-vh and --as-vv-top on :root in
 * sync with `window.visualViewport.height` and `.offsetTop`.
 *
 * iOS Safari's `100dvh` is unreliable when the soft keyboard +
 * form-helper-bar (the up/down/done strip iOS adds above the keyboard
 * on focus) are in play. The visual viewport API reports the true
 * visible area exactly, so we mirror it into CSS vars that the mobile
 * open-state rules read in place of `100dvh`.
 *
 * `--as-vv-top` carries `visualViewport.offsetTop`, the vertical
 * offset between the visual viewport (what the user actually sees)
 * and the layout viewport (what `position: fixed` is anchored to).
 * When iOS scrolls the layout viewport up to bring a focused input
 * above the keyboard, `offsetTop` becomes positive — without
 * compensating, the morph's `top: 0` (layout-viewport coords) ends up
 * above the visible area, and the user sees only the composer at the
 * top of their screen with no chat history. Pinning the morph to
 * `top: var(--as-vv-top)` keeps it aligned with what the user sees.
 *
 * Falls back to `100dvh` / `0` via the CSS defaults when the API is
 * unsupported (older iOS / Android).
 */

import { useEffect } from 'react'

export function useVisualViewportHeight(): void {
  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }
    const vv = window.visualViewport
    if (!vv) {
      return
    }
    const root = document.documentElement
    const update = () => {
      root.style.setProperty('--as-vh', `${vv.height}px`)
      root.style.setProperty('--as-vv-top', `${vv.offsetTop}px`)
    }
    update()
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    return () => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
    }
  }, [])
}
